import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Checkbox,
  Paper,
  Box,
  Typography,
  Alert,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import HoursInput from '../../components/common/HoursInput';
import { updateTimecard } from '../../services/timecardService';
import { checkMonthBoundary } from '../../utils/dateUtils';
import { getUserPtoHours } from '../../services/userService';
import { format } from 'date-fns';

const EditTimecardAdminDialog = ({ 
  open, 
  onClose, 
  timecard,
  onUpdate
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasHoursError, setHasHoursError] = useState(false);
  const [monthBoundaryInfo, setMonthBoundaryInfo] = useState(null);
  const [ptoBalance, setPtoBalance] = useState(null);
  const [originalTimecardHours, setOriginalTimecardHours] = useState(0);
  const [showPtoWarning, setShowPtoWarning] = useState(false);

  // Fetch user's PTO balance
  const fetchPtoBalance = useCallback(async () => {
    if (!timecard?.user?._id) {
      setPtoBalance(null);
      return;
    }
    
    try {
      const ptoHours = await getUserPtoHours(timecard.user._id);
      
      // Calculate available PTO hours
      const available = (
        parseFloat(ptoHours.yearStart || 0) +
        parseFloat(ptoHours.ytdAccumulated || 0) -
        parseFloat(ptoHours.ytdUsed || 0)
      ).toFixed(2);
      
      setPtoBalance(parseFloat(available));
    } catch (error) {
      console.error('Error fetching PTO balance:', error);
      setPtoBalance(null);
    }
  }, [timecard?.user?._id]);

  // Initialize days when timecard changes or dialog opens
  useEffect(() => {
    if (timecard && open) {
      // Check for month boundary using the raw date strings
      const boundaryInfo = checkMonthBoundary(
        timecard.startDate,
        timecard.endDate
      );
      setMonthBoundaryInfo(boundaryInfo);
      
      // Calculate original timecard hours for PTO validation
      // This is needed to add back to the balance when checking if new hours exceed available
      const originalHours = timecard.days.reduce((sum, day) => sum + parseFloat(day.hours || 0), 0);
      setOriginalTimecardHours(originalHours);
      
      const formattedDays = timecard.days.map(day => ({
        ...day,
        hours: parseFloat(day.hours || 0).toFixed(2),
        comments: day.comments || '',
        // Preserve certified status for approved days, since approved days were previously certified
        certified: day.approved ? true : (day.certified || false),
        approved: day.approved || false
      }));
      setDays(formattedDays);
      
      // Fetch PTO balance if this is a PTO project
      if (timecard.project && (
        timecard.project.name.includes('Leave') || 
        timecard.project.name.includes('ADMIN_PTO_SSL_LEAVE')
      )) {
        fetchPtoBalance();
      } else {
        // Reset PTO balance if not a PTO project
        setPtoBalance(null);
      }
    } else if (!open) {
      // Reset state when dialog is closed
      setDays([]);
      setMonthBoundaryInfo(null);
      setPtoBalance(null);
      setOriginalTimecardHours(0);
      setShowPtoWarning(false);
    }
  }, [timecard, open, fetchPtoBalance]);

  // Check for hours validation errors
  useEffect(() => {
    const hasError = days.some(day => parseFloat(day.hours || 0) > 24);
    setHasHoursError(hasError);
  }, [days]);

  // Check if total hours exceed PTO balance
  useEffect(() => {
    if (timecard && ptoBalance !== null && (
      timecard.project?.name.includes('Leave') || 
      timecard.project?.name.includes('ADMIN_PTO_SSL_LEAVE')
    )) {
      // For boundary weeks, only count hours from new month days
      // For non-boundary weeks, count all hours
      let totalHours;
      
      if (monthBoundaryInfo?.isMonthBoundary) {
        // Only count hours from new month days
        const newMonthDates = monthBoundaryInfo.newMonthDays;
        totalHours = days
          .filter(day => newMonthDates.includes(day.date))
          .reduce((sum, day) => sum + parseFloat(day.hours || 0), 0);
      } else {
        // Count all hours for non-boundary weeks
        totalHours = days.reduce((sum, day) => sum + parseFloat(day.hours || 0), 0);
      }
      
      // Add back the original timecard hours to the balance since they were already deducted
      // This gives us the effective available balance for this edit
      const effectiveBalance = ptoBalance + originalTimecardHours;
      
      // Show warning if hours exceed the effective balance
      setShowPtoWarning(totalHours > effectiveBalance);
    } else {
      setShowPtoWarning(false);
    }
  }, [days, ptoBalance, originalTimecardHours, timecard, monthBoundaryInfo]);

  const handleHoursChange = (index, value) => {
    const newDays = [...days];
    const hours = Math.max(0, parseFloat(value) || 0).toFixed(2);
    newDays[index] = { ...newDays[index], hours };
    setDays(newDays);
  };

  const handleCommentsChange = (index, value) => {
    const newDays = [...days];
    newDays[index] = { ...newDays[index], comments: value };
    setDays(newDays);
  };

  const handleCertifyChange = (index, checked) => {
    const newDays = [...days];
    newDays[index] = { ...newDays[index], certified: checked };
    setDays(newDays);
  };

  const handleCertifyAll = (checked) => {
    setDays(days.map(day => ({
      ...day,
      certified: checked
    })));
  };

  const handleApprovedChange = (index, checked) => {
    const newDays = [...days];
    newDays[index] = { ...newDays[index], approved: checked };
    setDays(newDays);
  };

  const handleApproveAll = (checked) => {
    setDays(days.map(day => ({
      ...day,
      approved: checked
    })));
  };

  // Helper function to parse UTC date string
  const parseUTCDate = (dateString) => {
    const date = new Date(dateString);
    return new Date(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate(),
      0, 0, 0, 0
    );
  };
  
  // Format date - mobile uses "MM/dd/yy" format, desktop uses "MMM d, yyyy" format
  const formatDate = (dateString) => {
    const date = parseUTCDate(dateString);
    if (isMobile) {
      return format(date, 'MM/dd/yy'); // e.g., "06/08/25"
    }
    return format(date, 'MMM d, yyyy'); // e.g., "Jun 8, 2025"
  };

  const getDayOfWeek = (index) => {
    return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][index];
  };

  const handleSave = async () => {
    // If this is a PTO project and hours exceed balance, prevent saving
    if (timecard && ptoBalance !== null && (
      timecard.project?.name.includes('Leave') || 
      timecard.project?.name.includes('ADMIN_PTO_SSL_LEAVE')
    )) {
      // For boundary weeks, only count hours from new month days
      // For non-boundary weeks, count all hours
      let totalHours;
      
      if (monthBoundaryInfo?.isMonthBoundary) {
        // Only count hours from new month days
        const newMonthDates = monthBoundaryInfo.newMonthDays;
        totalHours = days
          .filter(day => newMonthDates.includes(day.date))
          .reduce((sum, day) => sum + parseFloat(day.hours || 0), 0);
      } else {
        // Count all hours for non-boundary weeks
        totalHours = days.reduce((sum, day) => sum + parseFloat(day.hours || 0), 0);
      }
      
      // Add back the original timecard hours to the balance since they were already deducted
      const effectiveBalance = ptoBalance + originalTimecardHours;
      
      // Show warning if hours exceed the effective balance
      const exceedsPtoBalance = totalHours > effectiveBalance;
      setShowPtoWarning(exceedsPtoBalance);
      
      // Only proceed with save if hours don't exceed balance
      if (exceedsPtoBalance) {
        return; // Prevent saving
      }
    }
    
    setLoading(true);
    try {
      // Check certification and approval status
      const hasAnyCertified = days.some(day => day.certified);
      const hasAnyApproved = days.some(day => day.approved);
      
      let updatedStatus = timecard.status;
      
      // First, update the submitted flag for all certified days
      // Also preserve submitted status for approved days since they were previously submitted
      // This is crucial for proper status determination
      const updatedDays = days.map(day => ({
        ...day,
        submitted: (day.certified || day.approved) ? true : day.submitted
      }));
      
      // Determine status based on certification and approval
      if (!hasAnyCertified) {
        updatedStatus = 'Uncertified';
      } else if (days.every(day => day.certified)) {
        if (hasAnyApproved) {
          updatedStatus = days.every(day => day.approved) ? 'Approved' : 'Partially Approved';
        } else {
          updatedStatus = 'Certified';
        }
      } else {
        updatedStatus = 'Partially Certified';
      }

      const updatedTimecard = {
        ...timecard,
        days: updatedDays,
        status: updatedStatus
      };

      await updateTimecard(timecard._id, updatedTimecard, true);
      onUpdate(updatedTimecard);
      onClose();
    } catch (error) {
      console.error('Error updating timecard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!timecard) return null;

  const areAllDaysApproved = days.length > 0 && days.every(day => day.approved);
  const areSomeDaysApproved = days.some(day => day.approved);

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        {isMobile ? (
          <Box>
            <Typography variant="h6" component="div">Edit Timecard</Typography>
            <Typography variant="subtitle2" color="textSecondary" sx={{ mt: 0.5 }}>
              {timecard.project?.name}
            </Typography>
            <Typography variant="subtitle2" color="textSecondary">
              {timecard.user ? `${timecard.user.firstName} ${timecard.user.lastName}` : 'Unknown User'}
            </Typography>
          </Box>
        ) : (
          `Edit Timecard - ${timecard.project?.name} - ${timecard.user ? `${timecard.user.firstName} ${timecard.user.lastName}` : 'Unknown User'}`
        )}
      </DialogTitle>
      <DialogContent>
        {showPtoWarning && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            There are only {(ptoBalance + originalTimecardHours).toFixed(2)} remaining hours available to use.
          </Alert>
        )}
        
        {hasHoursError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Daily hours cannot exceed 24 hours. Please correct the highlighted fields.
          </Alert>
        )}
        
        {monthBoundaryInfo?.isMonthBoundary && (() => {
          // Check boundary week status for admin warning
          const endOfMonthDates = monthBoundaryInfo.endOfMonthDays;
          const endOfMonthDays = days.filter(d => 
            endOfMonthDates.includes(d.date)
          );
          const newMonthDates = monthBoundaryInfo.newMonthDays;
          const newMonthDays = days.filter(d => 
            newMonthDates.includes(d.date)
          );
          
          const endOfMonthCertifiedOrApproved = endOfMonthDays.every(d => 
            (d.certified && d.submitted) || d.approved
          );
          const newMonthHasHours = newMonthDays.some(d => 
            parseFloat(d.hours) > 0 || d.certified
          );
          
          // Show admin warning about boundary week constraints
          if (newMonthHasHours && !endOfMonthCertifiedOrApproved) {
            return (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <strong>Admin Notice:</strong> This is a month boundary week. Users typically cannot enter hours for new month days until end-of-month days are certified/approved. You have admin privileges to override this constraint.
              </Alert>
            );
          }
          
          // Show info about boundary week
          return (
            <Alert severity="info" sx={{ mb: 2 }}>
              <strong>Month Boundary Week:</strong> This timecard spans across months. End-of-month days are highlighted in orange, new month days in blue.
            </Alert>
          );
        })()}
        
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1">
            Week of {format(parseUTCDate(timecard.startDate), 'MMM d')} - {format(parseUTCDate(timecard.endDate), 'MMM d, yyyy')}
          </Typography>
          <Typography variant="subtitle2" color="textSecondary">
            Status: {timecard.status}
          </Typography>
        </Box>
        <TableContainer component={Paper}>
          <Table 
            size="small"
            sx={isMobile ? {
              '& .MuiTableCell-root:nth-of-type(1)': { width: '15% !important' },
              '& .MuiTableCell-root:nth-of-type(2)': { width: '15% !important' },
              '& .MuiTableCell-root:nth-of-type(3)': { width: '8% !important' },
              '& .MuiTableCell-root:nth-of-type(4)': { width: '15% !important' },
              '& .MuiTableCell-root:nth-of-type(5)': { width: '10% !important' },
              '& .MuiTableCell-root:nth-of-type(6)': { width: '10% !important' },
              tableLayout: 'fixed !important',
              width: '100% !important'
            } : {}}
          >
            <TableHead>
              <TableRow>
                <TableCell sx={isMobile ? { fontSize: '0.75rem', width: '15%' } : {}}>Date</TableCell>
                <TableCell sx={isMobile ? { fontSize: '0.75rem', width: '15%' } : {}}>Hours</TableCell>
                <TableCell sx={isMobile ? { fontSize: '0.75rem', width: '8%' } : {}}>{isMobile ? 'Day' : 'Day of Week'}</TableCell>
                <TableCell sx={isMobile ? { fontSize: '0.75rem', width: '15%' } : {}}>Comments</TableCell>
                <TableCell align="center" sx={isMobile ? { fontSize: '0.75rem', width: '10%' } : {}}>
                  {isMobile ? 'Cert' : 'Certify'}
                  <Checkbox
                    checked={days.length > 0 && days.every(day => day.certified)}
                    indeterminate={days.some(day => day.certified) && !days.every(day => day.certified)}
                    onChange={(e) => handleCertifyAll(e.target.checked)}
                    tabIndex={-1}
                    sx={isMobile ? { 
                      padding: '2px',
                      minWidth: '32px',
                      minHeight: '32px',
                      '& .MuiSvgIcon-root': {
                        fontSize: '1rem'
                      }
                    } : { padding: '0 0 0 4px' }}
                  />
                </TableCell>
                <TableCell align="center" sx={isMobile ? { fontSize: '0.75rem', width: '10%' } : {}}>
                  {isMobile ? 'App' : 'Approve'}
                  <Checkbox
                    checked={areAllDaysApproved}
                    indeterminate={areSomeDaysApproved && !areAllDaysApproved}
                    onChange={(e) => handleApproveAll(e.target.checked)}
                    tabIndex={-1}
                    sx={isMobile ? { 
                      padding: '2px',
                      minWidth: '32px',
                      minHeight: '32px',
                      '& .MuiSvgIcon-root': {
                        fontSize: '1rem'
                      }
                    } : { padding: '0 0 0 4px' }}
                  />
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {days.map((day, index) => (
                <TableRow 
                  key={day.date}
                  sx={{
                    // Add visual indicator for month boundary days
                    ...(monthBoundaryInfo?.isMonthBoundary && {
                      borderLeft: (theme) => {
                        const isEndOfMonth = monthBoundaryInfo.endOfMonthDays.includes(day.date);
                        const isNewMonth = monthBoundaryInfo.newMonthDays.includes(day.date);
                        if (isEndOfMonth) {
                          return `4px solid ${theme.palette.warning.main}`;
                        } else if (isNewMonth) {
                          return `4px solid ${theme.palette.info.main}`;
                        }
                        return 'none';
                      }
                    })
                  }}
                >
                  <TableCell sx={isMobile ? { fontSize: '0.75rem', padding: '4px', width: '15%' } : {}}>{formatDate(day.date)}</TableCell>
                  <TableCell sx={isMobile ? { padding: '2px', width: '15%' } : {}}>
                    <HoursInput
                      value={day.hours}
                      onChange={(value) => handleHoursChange(index, value)}
                      disabled={false}
                    />
                  </TableCell>
                  <TableCell sx={isMobile ? { fontSize: '0.75rem', padding: '4px', width: '8%' } : {}}>{getDayOfWeek(index)}</TableCell>
                  <TableCell sx={isMobile ? { padding: '2px', width: '15%' } : {}}>
                    <TextField
                      value={day.comments || ''}
                      onChange={(e) => handleCommentsChange(index, e.target.value)}
                      size="small"
                      fullWidth
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          ...(isMobile && {
                            minHeight: 'unset !important',
                            height: 'auto !important',
                            '& input': {
                              padding: '8px 4px !important', // Force exact match to HoursInput
                              fontSize: '0.75rem !important',
                              height: 'auto !important',
                              minHeight: 'unset !important',
                            }
                          })
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell align="center" sx={isMobile ? { padding: '4px', width: '10%' } : {}}>
                    <Checkbox
                      checked={day.certified || false}
                      onChange={(e) => handleCertifyChange(index, e.target.checked)}
                      tabIndex={-1}
                      sx={isMobile ? { 
                        padding: '2px',
                        minWidth: '32px',
                        minHeight: '32px',
                        '& .MuiSvgIcon-root': {
                          fontSize: '1rem'
                        }
                      } : { padding: '4px' }}
                    />
                  </TableCell>
                  <TableCell align="center" sx={isMobile ? { padding: '4px', width: '10%' } : {}}>
                    <Checkbox
                      checked={day.approved || false}
                      onChange={(e) => handleApprovedChange(index, e.target.checked)}
                      tabIndex={-1}
                      sx={isMobile ? { 
                        padding: '2px',
                        minWidth: '32px',
                        minHeight: '32px',
                        '& .MuiSvgIcon-root': {
                          fontSize: '1rem'
                        }
                      } : { padding: '4px' }}
                    />
                  </TableCell>
                </TableRow>
              ))}
              {/* Total row */}
              <TableRow sx={{ 
                borderTop: '2px solid',
                borderTopColor: 'divider',
                bgcolor: '#fff3cd'
              }}>
                <TableCell sx={isMobile ? { fontSize: '0.75rem', padding: '4px', width: '15%' } : {}}>Total</TableCell>
                <TableCell sx={isMobile ? { padding: '2px', width: '15%' } : {}}>
                  <Box sx={{ 
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: isMobile ? '32px' : '40px',
                    fontSize: isMobile ? '0.75rem' : '1rem',
                    fontWeight: 'bold'
                  }}>
                    {days.reduce((sum, day) => sum + parseFloat(day.hours || 0), 0).toFixed(2)}
                  </Box>
                </TableCell>
                <TableCell sx={isMobile ? { fontSize: '0.75rem', padding: '4px', width: '8%' } : {}}></TableCell>
                <TableCell sx={isMobile ? { padding: '2px', width: '15%' } : {}}></TableCell>
                <TableCell sx={isMobile ? { padding: '4px', width: '10%' } : {}}></TableCell>
                <TableCell sx={isMobile ? { padding: '4px', width: '10%' } : {}}></TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 0, justifyContent: 'flex-end' }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          onClick={handleSave} 
          variant="contained" 
          color="primary"
          disabled={loading || hasHoursError || showPtoWarning}
          sx={{ ml: 1 }}
        >
          {loading ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditTimecardAdminDialog;
